import { reportError } from "@/lib/apiUtils";
import { isTransientOutboundError } from "@/lib/transientError";
import { CookieName } from "@/types/nextCookie";
import { signIn } from "next-auth/react";

// These return the `signIn` promise so callers can `await` it and `.catch` a
// hard failure (e.g. a rejected fetch) to surface a loading/error state. On a
// successful request next-auth navigates away and the promise effectively
// never resolves; the rejection path only fires on a real failure. Returning
// the promise is backward-compatible with callers that ignore the return value.
export const googleSignIn = (callbackUrl?: string): Promise<unknown> =>
  signIn("google", callbackUrl ? { callbackUrl } : undefined);

export const azureSignIn = (callbackUrl?: string): Promise<unknown> =>
  signIn("azure-ad", callbackUrl ? { callbackUrl } : undefined);

export const yahooSignIn = (callbackUrl?: string): Promise<unknown> =>
  signIn("yahoo", callbackUrl ? { callbackUrl } : undefined);

export const emailSignIn = async (
  email: string,
  callbackUrl?: string,
  returnToUrl?: string
): Promise<void> => {
  let result;
  try {
    result = await signIn("email", {
      email,
      // returnToUrl is the app-level post-auth destination; when provided it
      // supersedes callbackUrl so NextAuth embeds the right URL in the
      // verification email link.
      callbackUrl: returnToUrl || callbackUrl || "/",
      redirect: false,
    });
  } catch (err) {
    // next-auth's client parses the response before returning, so a server
    // failure lands here too: an empty 5xx body throws a SyntaxError and a
    // relative redirect throws "Invalid URL". Only a failed fetch is the
    // traveler's connection. Intentional denials (blocked, already linked)
    // arrive as that relative redirect, so the fallback promises nothing.
    // Preserve the original for debugging.
    console.error("Email sign-in request failed:", err); // eslint-disable-line no-console
    reportError(err instanceof Error ? err : new Error(String(err)));
    throw new Error(
      isTransientOutboundError(err)
        ? "Sign-in request failed. Please check your connection and try again."
        : "Something went wrong on our end. Please try again, or email concierge@gondola.ai for help."
    );
  }

  if (!result) {
    // NextAuth returned undefined: request finished but with no result (e.g. tab closed, navigated away).
    throw new Error(
      "Sign-in didn't complete. Please check your connection and try again."
    );
  }

  if (result.ok && result.url) {
    // Mark this browser as the one that initiated sign-in.
    // The /auth/verify page checks for this cookie: if present, it auto-proceeds
    // to the NextAuth callback without requiring a button click. If absent (e.g.
    // different device, or a scanner pre-fetching the link), the user sees a
    // "Sign in to Gondola" button instead — scanners don't click buttons.
    document.cookie = `${CookieName.EMAIL_AUTH_PENDING}=1; path=/; max-age=86400; SameSite=Lax`;
    window.location.assign(result.url);
    return;
  }

  // NextAuth returns "EmailSignin" when server email send fails; show a friendly message
  const errorMessage =
    result.error?.toLowerCase() === "emailsignin"
      ? "Failed to send verification email. Please try again."
      : result.error || "Email authentication failed";

  throw new Error(errorMessage);
};

/**
 * Sign in with email+password. For test environments only.
 *
 * - MUST NOT be used in production. Only works when the entered email matches
 *   NEXT_PUBLIC_TEST_USER_EMAIL and the server validates against TEST_USER_PASSWORD.
 * - Credentials are sent in the request body; use HTTPS only.
 * - Error messages can reveal whether the email or password was wrong; acceptable
 *   for test-only use. If this flow is ever expanded, add rate limiting and
 *   consider generic error messages to reduce enumeration risk.
 * - No rate limiting; restrict preview deployment access (e.g. Vercel Deployment
 *   Protection) to reduce brute-force risk.
 */
export const credentialsSignIn = async (
  email: string,
  password: string,
  callbackUrl?: string
): Promise<void> => {
  const result = await signIn("credentials", {
    email,
    password,
    callbackUrl: callbackUrl || "/",
    redirect: false,
  });

  if (!result) {
    throw new Error("Credentials sign-in did not return a result");
  }

  if (result.ok && result.url) {
    window.location.assign(result.url);
    return;
  }

  // NextAuth uses "CredentialsSignin" for any authorize() failure (wrong creds,
  // missing creds, or missing TEST_USER_EMAIL/TEST_USER_PASSWORD on server)
  const errorMessage =
    result.error === "CredentialsSignin"
      ? "Sign-in failed. Check your credentials and that test sign-in is configured (TEST_USER_EMAIL, TEST_USER_PASSWORD)."
      : result.error || "Sign-in failed";

  throw new Error(errorMessage);
};

// The *Sync / addNew* helpers below return the `signIn` promise (like the
// primary OAuth helpers) so callers can `await`/`.catch` and drive a loading /
// error state. Backward-compatible with the fire-and-forget callers that
// ignore the return value.
export const azureSignInSync = (
  removeLocationSearch = false,
  goToRateMonitoring = false,
  customCallbackUrl?: string
): Promise<unknown> => {
  const hasSearch = (removeLocationSearch ? "" : window.location.search) !== "";
  sessionStorage.setItem("emailSyncProvider", "microsoft");
  return signIn("azure-ad-scoped-auth", {
    callbackUrl:
      customCallbackUrl ||
      "/api/user/email/update_status" +
        (removeLocationSearch ? "" : window.location.search) +
        (goToRateMonitoring
          ? `${hasSearch ? "&" : "?"}showRoomRateMonitor=true`
          : ""),
  });
};

export const addNewazureAccount = (): Promise<unknown> =>
  signIn("azure-ad-scoped-auth", {
    callbackUrl: "/api/user/email/add_new_account",
  });

export const googleSignInSync = (
  removeLocationSearch = false,
  goToRateMonitoring = false,
  customCallbackUrl?: string
): Promise<unknown> => {
  const hasSearch = (removeLocationSearch ? "" : window.location.search) !== "";
  sessionStorage.setItem("emailSyncProvider", "google");
  return signIn("google-scoped-auth", {
    callbackUrl:
      customCallbackUrl ||
      "/api/user/email/update_status" +
        (removeLocationSearch ? "" : window.location.search) +
        (goToRateMonitoring
          ? `${hasSearch ? "&" : "?"}showRoomRateMonitor=true`
          : ""),
  });
};

export const addNewGoogleAccount = (): Promise<unknown> =>
  signIn(
    "google-scoped-auth",
    { callbackUrl: "/api/user/email/add_new_account" },
    { scope: "openid email https://www.googleapis.com/auth/gmail.readonly" }
  );

export const googleSignInReSync = (welcomeActionId: string, userId: string) => {
  return signIn("google-scoped-auth", {
    callbackUrl: `/api/user/email/re_sync?welcomeActionId=${welcomeActionId}&userId=${userId}`,
  });
};

export const azureSignInReSync = (welcomeActionId: string, userId: string) => {
  return signIn("azure-ad-scoped-auth", {
    callbackUrl: `/api/user/email/re_sync?welcomeActionId=${welcomeActionId}&userId=${userId}`,
  });
};

export const yahooSignInReSync = (welcomeActionId: string, userId: string) => {
  return signIn("yahoo-scoped-auth", {
    callbackUrl: `/api/user/email/re_sync?welcomeActionId=${welcomeActionId}&userId=${userId}`,
  });
};

export const yahooSignInSync = (
  removeLocationSearch = false,
  goToRateMonitoring = false,
  customCallbackUrl?: string
): Promise<unknown> => {
  const hasSearch = (removeLocationSearch ? "" : window.location.search) !== "";
  sessionStorage.setItem("emailSyncProvider", "yahoo");
  return signIn("yahoo-scoped-auth", {
    callbackUrl:
      customCallbackUrl ||
      "/api/user/email/update_status" +
        (removeLocationSearch ? "" : window.location.search) +
        (goToRateMonitoring
          ? `${hasSearch ? "&" : "?"}showRoomRateMonitor=true`
          : ""),
  });
};

export const addNewYahooAccount = (): Promise<unknown> =>
  signIn("yahoo-scoped-auth", {
    callbackUrl: "/api/user/email/add_new_account",
  });
